import { OccasionalSlotsValidationErrors } from '../../models/occasionalSlotsValidation'
import { OccasionalSlotFormProps } from '../types'

export type FormType = OccasionalSlotFormProps['type'] // 'add' | 'edit' | 'multiEdit'

type Placement = 'line' | 'popup' | false

const ON_ALL_FORMS = {
    add: 'line',
    edit: 'line',
    multiEdit: 'line'
} as const satisfies Record<FormType, Placement>

export const PLACEMENT_BY_BLOCK = {
    dateSelection: ON_ALL_FORMS,
    timeSelection: ON_ALL_FORMS,
    slotCount: { add: 'line', edit: false, multiEdit: 'popup' },
    sectionSelection: ON_ALL_FORMS,
    prioritySelection: ON_ALL_FORMS,
    confirmedChildrenSelection: ON_ALL_FORMS,
    expirationSelection: ON_ALL_FORMS,
    scheduleSelection: { add: false, edit: 'line', multiEdit: 'line' },
    rpePlacesSelection: ON_ALL_FORMS,
    placesSelection: ON_ALL_FORMS,
    internalNoteField: ON_ALL_FORMS,
    externalNoteField: ON_ALL_FORMS
} as const satisfies Record<string, Record<FormType, Placement>>

export type BlockKey = keyof typeof PLACEMENT_BY_BLOCK

type BlocksPlacedAs<P extends Placement, T extends FormType> = {
    [B in BlockKey]: (typeof PLACEMENT_BY_BLOCK)[B][T] extends P ? B : never
}[BlockKey]

export type FormBlock<T extends FormType> = BlocksPlacedAs<'line', T>

export type AnyFormBlock = { [K in FormType]: FormBlock<K> }[FormType]

export type ConfirmPopup<T extends FormType> = BlocksPlacedAs<'popup', T>

export type AnyConfirmPopup = { [K in FormType]: ConfirmPopup<K> }[FormType]

export type BlockLine<T extends FormType> = [FormBlock<T>] | [FormBlock<T>, FormBlock<T>]

export type AnyBlockLine = { [K in FormType]: BlockLine<K> }[FormType]

export interface FormLayout<T extends FormType> {
    lines: BlockLine<T>[]
    moreLines: BlockLine<T>[]
    confirmPopUp?: ConfirmPopup<T>
}

export type AnyFormLayout = { [K in FormType]: FormLayout<K> }[FormType]

// Types to ensure that every block is where it is supposed to and not missing
type LayoutBlocks<FL extends FormLayout<FormType>> = FL['lines'][number][number] | FL['moreLines'][number][number]

type LayoutPopup<FL extends FormLayout<FormType>> = FL extends { confirmPopUp: infer P } ? P : never

type MustBeComplete<FT extends FormType, FL extends FormLayout<FormType>> = [
    Exclude<FormBlock<FT>, LayoutBlocks<FL>> | Exclude<ConfirmPopup<FT>, LayoutPopup<FL>>
] extends [never]
    ? unknown
    : { missing: Exclude<FormBlock<FT>, LayoutBlocks<FL>> | Exclude<ConfirmPopup<FT>, LayoutPopup<FL>> }

function defineLayout<T extends FormType, L extends FormLayout<T>>(_type: T, layout: L & MustBeComplete<T, L>): L {
    return layout
}

export const LAYOUT_BY_TYPE: { [K in FormType]: FormLayout<K> } = {
    add: defineLayout('add', {
        lines: [['dateSelection'], ['timeSelection'], ['slotCount', 'sectionSelection'], ['rpePlacesSelection']],
        moreLines: [
            ['prioritySelection', 'confirmedChildrenSelection'],
            ['expirationSelection', 'placesSelection'],
            ['internalNoteField'],
            ['externalNoteField']
        ]
    }),
    edit: defineLayout('edit', {
        lines: [['dateSelection', 'sectionSelection'], ['timeSelection'], ['rpePlacesSelection']],
        moreLines: [
            ['prioritySelection', 'confirmedChildrenSelection'],
            ['expirationSelection', 'scheduleSelection'],
            ['placesSelection'],
            ['internalNoteField'],
            ['externalNoteField']
        ]
    }),
    multiEdit: defineLayout('multiEdit', {
        lines: [['dateSelection', 'sectionSelection'], ['timeSelection'], ['rpePlacesSelection']],
        moreLines: [
            ['prioritySelection', 'confirmedChildrenSelection'],
            ['expirationSelection', 'scheduleSelection'],
            ['placesSelection'],
            ['internalNoteField'],
            ['externalNoteField']
        ],
        confirmPopUp: 'slotCount'
    })
}

export const ERROR_FIELD_BY_BLOCK: Partial<Record<BlockKey, keyof OccasionalSlotsValidationErrors>> = {
    dateSelection: 'date',
    timeSelection: 'time',
    slotCount: 'count',
    sectionSelection: 'section',
    confirmedChildrenSelection: 'children'
}
